import type { CSSProperties, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { SorareLogo } from '@sorare/core/src/atoms/icons/SorareLogo';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, BrandM, BrandXL } from '@sorare/core/src/atoms/typography';
import { FrameCorners } from '@sorare/core/src/atoms/ui/FrameCorners/index.tsx';
import { HideSeriesBottomNav } from '@sorare/core/src/components/navigation/AppNavigation/HideSeriesBottomNav';
import { css } from '@sorare/css';

import setIcon from '#src/assets/set.svg';
import squadImg from '#src/assets/squad.webp';

import { SquadContentBlock, SquadFooter } from '../ui';

const TITLE_GRADIENT = 'linear-gradient(180deg, #aa83ff 18%, #7d41ff 100%)';

const root = css`
  position: relative;
  isolation: isolate;
  min-height: calc(var(--100vh) - var(--current-stack-height));
  overflow: hidden;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    var(--c-blue-400) 300%
  );
`;

const frameCornersWrapper = css`
  position: absolute;
  top: var(--quadruple-unit);
  left: var(--double-unit);
  right: var(--double-unit);
  bottom: 0;
  pointer-events: none;
`;

const content = css`
  position: relative;
  z-index: 1;
  flex: 1;
  padding: var(--quadruple-unit) var(--double-unit);
`;

const squadLogo = css`
  object-fit: contain;
  max-height: 12vh;
`;

type Props = {
  title: ReactNode;
  squadName: string;
  children?: ReactNode;
  footer: ReactNode;
};

export const SquadCelebration = ({
  title,
  squadName,
  children,
  footer,
}: Props) => {
  return (
    <Vertical className={root}>
      <div className={frameCornersWrapper}>
        <FrameCorners topOnly />
      </div>
      <SquadContentBlock>
        <Vertical gap={3} className={content}>
          <Horizontal gap={0.5} center>
            <SorareLogo size="small" />
            <img src={setIcon} alt="Set" />
          </Horizontal>
          <BrandXL
            className="text-gradient-color text-center"
            style={
              {
                '--text-gradient-color': TITLE_GRADIENT,
              } as CSSProperties
            }
          >
            {title}
          </BrandXL>
          <BodyL color="var(--c-white)" className="text-center">
            <FormattedMessage defaultMessage="Team up now for exclusive rewards" />
          </BodyL>
          <Vertical gap={2} center>
            <img
              src={squadImg}
              alt=""
              width={135}
              height={135}
              className={squadLogo}
            />
            <BrandM>{squadName}</BrandM>
            {children}
          </Vertical>
        </Vertical>
      </SquadContentBlock>
      <SquadFooter>{footer}</SquadFooter>
      <HideSeriesBottomNav />
    </Vertical>
  );
};
